// ============================================================================
// Component-scoped private farm-document URL owner
// ============================================================================
//
// PrivateStorageImage is mounted by several equipment surfaces. It must not
// mint a URL in a component effect: this owner gives every allowlisted object a
// full authenticated-scope Query key, dedupes repeated thumbnails, and refreshes
// the ten-minute server URL before it expires. It is deliberately not a route
// resource; privateStorageUrlRegistry receipts its exact shared-component
// mounts without claiming route ownership or boot independence.
// ============================================================================

import {skipToken, useQuery} from '@tanstack/react-query';
import type {QueryKey as TanStackQueryKey, UseQueryOptions} from '@tanstack/react-query';
import {useMemo} from 'react';

import type {AuthState} from '../contexts/AuthContext.jsx';
import {
  PRIVATE_FARM_DOCUMENT_BUCKETS,
  createPrivateStorageSignedUrl,
  resolvePrivateStoragePath,
} from '../lib/privateStorage.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {resourceKey, scopeOrNull, UNRESOLVED_RESOURCE_KEY} from '../app/data/routeDataCatalog.js';
import type {AuthenticatedScope} from '../app/data/routeDataCatalog.js';
import {PRIVATE_STORAGE_URL_RESOURCE} from './privateStorageUrlRegistry.js';

// The existing createPrivateStorageSignedUrl contract permits at most ten
// minutes. The query owner's refresh scheduling is about that server lifetime,
// not a decision to cache farm records for a different period.
export const PRIVATE_STORAGE_SIGNED_URL_TTL_SECONDS = 600;
export const PRIVATE_STORAGE_SIGNED_URL_TTL_MS = PRIVATE_STORAGE_SIGNED_URL_TTL_SECONDS * 1000;
export const PRIVATE_STORAGE_SIGNED_URL_REFRESH_LEAD_MS = 30 * 1000;
export const PRIVATE_STORAGE_SIGNED_URL_REFRESH_AFTER_MS =
  PRIVATE_STORAGE_SIGNED_URL_TTL_MS - PRIVATE_STORAGE_SIGNED_URL_REFRESH_LEAD_MS;
const MIN_REFRESH_DELAY_MS = 1_000;

export type PrivateStorageUrlInput = {
  readonly bucket: unknown;
  readonly path?: unknown;
  readonly legacyUrl?: unknown;
};

export type NormalizedPrivateStorageUrlInput = {
  readonly cacheKey: string;
  readonly bucket: string;
  readonly path: string;
};

export type PrivateStorageSignedUrl = {
  readonly url: string;
  // Capture this before signing starts. A slow connection must not make a URL
  // look newly minted when most of its server lifetime has already passed.
  readonly issuedAt: number;
  readonly expiresAt: number;
};

export type PrivateStorageUrlProjection = {
  readonly url: string | null;
  readonly loading: boolean;
  readonly error: unknown;
  readonly path: string | null;
};

type PrivateStorageUrlQueryOption = UseQueryOptions<
  PrivateStorageSignedUrl,
  Error,
  PrivateStorageSignedUrl,
  TanStackQueryKey
>;

type PrivateStorageUrlQueryLike = {
  readonly data: PrivateStorageSignedUrl | undefined;
  readonly isPending: boolean;
  readonly isFetching: boolean;
  readonly error: unknown;
};

const FILE_PATH_UNAVAILABLE = new Error('File path is unavailable.');
const FILE_AUTHORIZATION_UNAVAILABLE = new Error('Could not load file.');

function isPrivateFarmDocumentBucket(bucket: unknown): bucket is string {
  return typeof bucket === 'string' && PRIVATE_FARM_DOCUMENT_BUCKETS.some((allowed) => allowed === bucket);
}

/**
 * Resolve persisted image metadata to the one safe bucket/path selector that
 * will reach Storage. The shared legacy-URL fallback stays exactly in
 * resolvePrivateStoragePath, and malformed metadata never produces a Query.
 */
export function normalizePrivateStorageUrlInput(
  input: PrivateStorageUrlInput | null | undefined,
): NormalizedPrivateStorageUrlInput | null {
  if (!input || !isPrivateFarmDocumentBucket(input.bucket)) return null;
  const path = resolvePrivateStoragePath(input.bucket, input.path, input.legacyUrl);
  if (!path) return null;
  return {cacheKey: `${input.bucket}:${path}`, bucket: input.bucket, path};
}

/** Private media URLs have no public/signed-out cache namespace. */
export function privateStorageScope(authState: AuthState, rolePreview?: unknown): AuthenticatedScope | null {
  const scope = scopeOrNull(authState, rolePreview);
  return scope && scope.kind === 'authenticated' ? scope : null;
}

function throwIfAborted(signal: AbortSignal): void {
  // Storage.createSignedUrl accepts no signal. Reading Query's signal still
  // lets Query retire an orphaned observer; the checks prevent a completed
  // abandoned request from becoming usable under its old scope key.
  if (signal.aborted) throw new Error('Private file signing was aborted.');
}

/** The one automatic signer used by every mounted PrivateStorageImage. */
export async function loadPrivateStorageSignedUrl(
  client: SupabaseClient,
  input: NormalizedPrivateStorageUrlInput,
  signal: AbortSignal,
  now: () => number = Date.now,
): Promise<PrivateStorageSignedUrl> {
  throwIfAborted(signal);
  const issuedAt = now();
  const url = await createPrivateStorageSignedUrl(
    client,
    input.bucket,
    input.path,
    PRIVATE_STORAGE_SIGNED_URL_TTL_SECONDS,
  );
  throwIfAborted(signal);
  return {url, issuedAt, expiresAt: issuedAt + PRIVATE_STORAGE_SIGNED_URL_TTL_MS};
}

/** Time until the pre-expiry refresh, based on the URL's real issuance time. */
export function privateStorageRefreshDelay(
  data: PrivateStorageSignedUrl | undefined,
  now: number = Date.now(),
): number | false {
  if (!data || !data.url || !Number.isFinite(data.expiresAt)) return false;
  return Math.max(MIN_REFRESH_DELAY_MS, data.expiresAt - now - PRIVATE_STORAGE_SIGNED_URL_REFRESH_LEAD_MS);
}

/** Keep expired private URLs out of image elements while their Query refreshes. */
export function privateStorageUrlProjection(
  query: PrivateStorageUrlQueryLike,
  input: NormalizedPrivateStorageUrlInput | null,
  ready: boolean,
  authPending: boolean,
  now: number = Date.now(),
): PrivateStorageUrlProjection {
  if (!input) {
    return {url: null, loading: false, error: FILE_PATH_UNAVAILABLE, path: null};
  }
  if (!ready) {
    // The previous effect began in a loading state while auth was resolving.
    // Keep that exact visible fallback but do not mint under an unscoped key.
    return {
      url: null,
      loading: authPending,
      error: authPending ? null : FILE_AUTHORIZATION_UNAVAILABLE,
      path: input.path,
    };
  }

  const data = query.data;
  const url = data && data.expiresAt > now ? data.url : null;
  return {
    url,
    loading: Boolean(!url && (query.isPending || query.isFetching)),
    error: query.error ?? null,
    path: input.path,
  };
}

export function privateStorageUrlQueryKey(scope: AuthenticatedScope, input: NormalizedPrivateStorageUrlInput) {
  // TTL belongs to identity rather than only to the query policy: a future
  // call site that legitimately needs a different URL lifetime cannot reuse a
  // longer-lived entry by accident.
  return resourceKey(PRIVATE_STORAGE_URL_RESOURCE.SIGNED_URL, scope, {
    bucket: input.bucket,
    path: input.path,
    ttlSeconds: PRIVATE_STORAGE_SIGNED_URL_TTL_SECONDS,
  });
}

function privateStorageUrlQueryOptions({
  client,
  scope,
  input,
}: {
  client: SupabaseClient;
  scope: AuthenticatedScope | null;
  input: NormalizedPrivateStorageUrlInput | null;
}): PrivateStorageUrlQueryOption {
  if (!scope || !input) {
    // Never leave a runnable function on a public/unresolved identity. The
    // reviewed inert key is shared only by disabled, skipped observers.
    return {queryKey: UNRESOLVED_RESOURCE_KEY, queryFn: skipToken, enabled: false};
  }

  return {
    queryKey: privateStorageUrlQueryKey(scope, input),
    // Reading signal is load-bearing for Query's orphan cancellation. The SDK
    // has no signal option, so loadPrivateStorageSignedUrl checks either side
    // of the signing call as well.
    queryFn: ({signal}: {signal: AbortSignal}) => loadPrivateStorageSignedUrl(client, input, signal),
    enabled: true,
    staleTime: PRIVATE_STORAGE_SIGNED_URL_REFRESH_AFTER_MS,
    refetchInterval: (query: {state: {data: PrivateStorageSignedUrl | undefined}}) =>
      privateStorageRefreshDelay(query.state.data),
    // A background tab does not continuously mint a private URL. Returning to
    // it refetches only after the pre-expiry stale boundary has passed.
    refetchOnWindowFocus: true,
  };
}

/**
 * Shared Query owner for automatic private document image URLs. The wrapper
 * component supplies current AuthContext identity; user-click open/link flows
 * intentionally keep their direct fresh signer in PrivateStorageObject.
 */
export function usePrivateStorageSignedUrl({
  client,
  authState,
  rolePreview,
  bucket,
  path,
  legacyUrl,
}: {
  client: SupabaseClient;
  authState: AuthState;
  rolePreview?: unknown;
  bucket: unknown;
  path?: unknown;
  legacyUrl?: unknown;
}): PrivateStorageUrlProjection {
  const input = useMemo(() => normalizePrivateStorageUrlInput({bucket, path, legacyUrl}), [bucket, legacyUrl, path]);
  const scope = useMemo(() => privateStorageScope(authState, rolePreview), [authState, rolePreview]);
  const ready = Boolean(scope && input);
  const options = useMemo<PrivateStorageUrlQueryOption>(
    () => privateStorageUrlQueryOptions({client, scope, input}),
    [client, input, scope],
  );
  const query = useQuery(options);

  return useMemo(
    () => privateStorageUrlProjection(query, input, ready, authState === null),
    [authState, input, query, ready],
  );
}
